import { useEffect, useRef, useState } from 'react';
import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
  TransitionChild,
} from '@headlessui/react';
import { Link, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import {
  ArchiveBoxIcon,
  ArchiveBoxXMarkIcon,
  ArrowRightStartOnRectangleIcon,
  Bars3Icon,
  CheckIcon,
  ChevronDownIcon,
  ClockIcon,
  DocumentTextIcon,
  EyeSlashIcon,
  Squares2X2Icon,
  TrashIcon,
  UserCircleIcon,
  UserGroupIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useAtom, useAtomValue } from 'jotai';
import { NotificationType } from '@components/Notification/Notification';
import { MacroSearch } from '@components/MacroSearch';
import { NotificationErrorBoundary } from '@views/ErrorBoundary';
import { FLOX_LOGO_BLUE_SRC, FLOX_ICON_BLUE_SRC } from '../../constants';
import { useAuth } from '../../context';
import {
  notificationsAtom,
  unreadNotificationsCountAtom,
  inboxCategoryFilterAtom,
  notificationPreferencesAtom,
  sidebarCollapsedAtom,
} from '../../store/jotai';
import { getNotificationCategory } from '../../constants/notificationCategories';
import { useNovuNotifications, UserRoleService } from '../../services';
import { NavigationItem, NavigationItemList } from './types';
import { CollapseIcon, ExpandIcon } from './icons';
import { InboxCategoryTabs } from './InboxCategoryTabs';

const userNavigation = [
  {
    name: 'Account profile',
    href: '/profile',
    icon: UserCircleIcon,
  },
  {
    name: 'Legal Documents',
    href: '/legal-documents',
    icon: DocumentTextIcon,
  },
  {
    name: 'Team',
    href: '/team',
    icon: UserGroupIcon,
  },
  {
    name: 'Integrations',
    href: '/integrations',
    icon: Squares2X2Icon,
  },
  {
    name: 'Log out',
    href: '#',
    icon: ArrowRightStartOnRectangleIcon,
  },
];

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ');
}

interface NavigationProps {
  items: NavigationItemList;
  children: React.ReactNode;
}

dayjs.extend(relativeTime);

function InboxItem({
  notification,
  notifications,
  isWarehouseClient,
  markAsRead,
  archiveNotification,
  unarchiveNotification,
  deleteNotification,
  snoozeNotification,
  setNotifications,
  setInboxOpen,
  showArchived,
}: {
  notification: NotificationType;
  notifications: NotificationType[];
  isWarehouseClient: boolean;
  markAsRead: (notificationId: string) => void;
  archiveNotification: (notificationId: string) => void;
  unarchiveNotification: (notificationId: string) => void;
  deleteNotification: (notificationId: string) => void;
  snoozeNotification: (notificationId: string, until: Date) => void;
  setNotifications: (notifications: NotificationType[]) => void;
  setInboxOpen: (isOpen: boolean) => void;
  showArchived: boolean;
}) {
  const [showSnoozeMenu, setShowSnoozeMenu] = useState(false);

  const snoozeOptions = [
    { label: '1 hour', hours: 1 },
    { label: '2 hours', hours: 2 },
    { label: '6 hours', hours: 6 },
    { label: '12 hours', hours: 12 },
    { label: '24 hours', hours: 24 },
  ];

  const handleSnooze = (hours: number) => {
    const until = new Date();
    until.setHours(until.getHours() + hours);
    snoozeNotification(notification.id, until);
    setShowSnoozeMenu(false);
  };

  const handleMarkAsReadClick = async (
    event: React.MouseEvent<HTMLButtonElement>,
    notificationId: string
  ) => {
    event.stopPropagation();
    markAsRead(notificationId);

    setNotifications(
      notifications.map((n) => {
        if (n.id === notificationId) {
          return { ...n, readAt: new Date().toISOString() };
        }
        return n;
      })
    );
  };

  const handleMarkAsArchivedClick = async (
    event: React.MouseEvent<HTMLButtonElement>,
    notificationId: string
  ) => {
    event.stopPropagation();
    archiveNotification(notificationId);
  };

  const handleUnarchiveClick = async (
    event: React.MouseEvent<HTMLButtonElement>,
    notificationId: string
  ) => {
    event.stopPropagation();
    unarchiveNotification(notificationId);

    setNotifications(
      notifications.map((n) => {
        if (n.id === notificationId) {
          return { ...n, archivedAt: null };
        }
        return n;
      })
    );
  };

  const handleDeleteClick = async (
    event: React.MouseEvent<HTMLButtonElement>,
    notificationId: string
  ) => {
    event.stopPropagation();
    deleteNotification(notificationId);
  };

  const buildNotificationPath = () => {
    const notificationTypePrefix = notification.notificationType.split('.')[0];
    const notificationTypeSuffix = notification.notificationType.split('.')[1];

    switch (notificationTypePrefix) {
      case 'Warehouse':
      case 'WarehouseMatch':
        if (notificationTypeSuffix === 'ShortlistCreated') {
          return `/warehouse-shortlist`;
        }
        if (notificationTypePrefix === 'WarehouseMatch' && !isWarehouseClient) {
          return `/customers`;
        }
        if (notificationTypeSuffix === 'Proposed') {
          return `/warehouse/${notification.relatedEntities[0].id}/service-rates`;
        }
        return `/warehouse/${notification.relatedEntities[0].id}`;
      case 'InventoryProduct':
        return isWarehouseClient ? `/product-sku-master` : `/shipments`;
      case 'InventorySku':
        return `/shipments`;
      case 'SupplyChainProcess':
        return `/noticeboard`;
      case 'SalesOrder':
        return `/noticeboard`;
      case 'Vehicle':
        return `/fleet-master`;
      default:
        return `/noticeboard`;
    }
  };

  const buildNotificationSearch = () => {
    const notificationTypePrefix = notification.notificationType.split('.')[0];
    const notificationTypeSuffix = notification.notificationType.split('.')[1];

    switch (notificationTypePrefix) {
      case 'WarehouseMatch': {
        if (
          notificationTypeSuffix === 'ShortlistCreated' ||
          isWarehouseClient
        ) {
          return ``;
        }
        const warehouseMatchId = notification.relatedEntities?.find(
          (entity) => entity.type === 'WarehouseMatch'
        )?.id;
        return warehouseMatchId ? `?matchId=${warehouseMatchId}` : ``;
      }
      case 'InventoryProduct':
        return isWarehouseClient
          ? `?productId=${notification.relatedEntities[0].id}`
          : ``;
      case 'SupplyChainProcess':
        switch (notificationTypeSuffix) {
          case 'PickingNoteConfirmed':
            return `?supplyChainProcessId=${notification.relatedEntities[0].id}&processChildType=${notificationTypeSuffix}`;
          case 'GoodsReceivedNoteFinalised':
            return `?supplyChainProcessId=${notification.relatedEntities[0].id}&processChildType=${notificationTypeSuffix}`;
          default:
            return `?supplyChainProcessId=${notification.relatedEntities[0].id}`;
        }
      case 'Warehouse':
      case 'InventorySku':
      case 'Vehicle':
      default:
        return ``;
    }
  };

  const isRead = !!notification?.readAt;
  const isArchived = !!notification?.archivedAt;

  return (
    <div
      className={`
        relative px-4 py-3 border-b border-gray-100
        ${isArchived ? 'opacity-60' : ''}
        ${!isRead && !isArchived ? 'bg-blue-50' : ''}
        hover:bg-gray-50
      `}
    >
      {!isRead && (
        <div className='absolute left-0 top-3 bottom-3 w-1 bg-primary rounded-r' />
      )}

      <Link
        to={{
          pathname: buildNotificationPath(),
          search: buildNotificationSearch(),
        }}
        onClick={() => setInboxOpen(false)}
        reloadDocument
        className='block'
      >
        <div className='flex items-start justify-between gap-2'>
          <div className='flex-1 min-w-0'>
            <h4
              className={`text-sm truncate ${
                !isRead
                  ? 'font-semibold text-gray-900'
                  : 'font-medium text-gray-600'
              }`}
            >
              {notification.title}
            </h4>
            <p className='text-xs text-gray-500 mt-0.5 line-clamp-2'>
              {notification.message}
            </p>
          </div>
          <span className='flex-shrink-0 text-xs text-gray-400 whitespace-nowrap'>
            {dayjs(notification.createdAt).fromNow()}
          </span>
        </div>
      </Link>

      <div className='flex items-center gap-1 mt-2'>
        {!isArchived ? (
          <>
            {!isRead && (
              <>
                <button
                  type='button'
                  className='inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors'
                  onClick={(e) => handleMarkAsReadClick(e, notification.id)}
                >
                  <CheckIcon className='h-3.5 w-3.5' />
                  Read
                </button>
                <div className='relative'>
                  <button
                    type='button'
                    className='inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors'
                    onClick={(e) => {
                      e.stopPropagation();
                      setShowSnoozeMenu(!showSnoozeMenu);
                    }}
                  >
                    <ClockIcon className='h-3.5 w-3.5' />
                    Snooze
                  </button>
                  {showSnoozeMenu && (
                    <div className='absolute left-0 top-full mt-1 bg-white border border-gray-200 rounded-md shadow-lg py-1 z-10 min-w-[100px]'>
                      {snoozeOptions.map((option) => (
                        <button
                          key={option.hours}
                          type='button'
                          className='block w-full text-left px-3 py-1.5 text-xs text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                          onClick={(e) => {
                            e.stopPropagation();
                            handleSnooze(option.hours);
                          }}
                        >
                          {option.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </>
            )}
            <button
              type='button'
              className='inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors'
              onClick={(e) => handleMarkAsArchivedClick(e, notification.id)}
            >
              <ArchiveBoxIcon className='h-3.5 w-3.5' />
              Archive
            </button>
          </>
        ) : (
          <button
            type='button'
            className='inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-gray-700 hover:bg-gray-100 rounded transition-colors'
            onClick={(e) => handleUnarchiveClick(e, notification.id)}
          >
            <ArchiveBoxXMarkIcon className='h-3.5 w-3.5' />
            Restore
          </button>
        )}
        <button
          type='button'
          className='inline-flex items-center gap-1 px-2 py-1 text-xs text-gray-500 hover:text-red-600 hover:bg-red-50 rounded transition-colors'
          onClick={(e) => handleDeleteClick(e, notification.id)}
        >
          <TrashIcon className='h-3.5 w-3.5' />
          Delete
        </button>
      </div>
    </div>
  );
}

interface NavMenuItemProps {
  item: NavigationItem;
  isActive: boolean;
  badgeCount?: number;
  onClick?: () => void;
  collapsed?: boolean;
}

function NavMenuItem({
  item,
  isActive,
  badgeCount,
  onClick,
  collapsed,
}: NavMenuItemProps) {
  const Icon = item.icon;

  const content = (
    <>
      {/* Active indicator */}
      <span
        className={classNames(
          'absolute left-0 top-0 bottom-0 w-1 rounded-r',
          isActive ? 'bg-primary' : 'bg-transparent'
        )}
      />
      {Icon && (
        <Icon
          className={classNames(
            'h-5 w-5 shrink-0',
            isActive ? 'text-primary' : 'text-gray-500'
          )}
          aria-hidden='true'
        />
      )}
      {!collapsed && <span className='truncate'>{item.label}</span>}
      {!collapsed && badgeCount !== undefined && badgeCount > 0 && (
        <span className='ml-auto inline-flex items-center justify-center h-5 min-w-[20px] px-1.5 rounded-full bg-primary text-xs font-medium text-white'>
          {badgeCount}
        </span>
      )}
      {collapsed && badgeCount !== undefined && badgeCount > 0 && (
        <span className='absolute -top-1 -right-1 inline-flex items-center justify-center h-4 min-w-[16px] px-1 rounded-full bg-primary text-[10px] font-medium text-white'>
          {badgeCount}
        </span>
      )}
    </>
  );

  const baseClasses = collapsed
    ? 'group relative flex items-center justify-center py-2 px-2 text-sm font-medium leading-6 transition-colors rounded-md'
    : 'group relative flex items-center gap-x-3 py-2 px-3 text-sm font-medium leading-6 transition-colors';
  const activeClasses = isActive
    ? 'bg-blue-100 text-primary'
    : 'text-gray-700 hover:bg-gray-50 hover:text-gray-900';

  if (onClick) {
    return (
      <button
        type='button'
        onClick={onClick}
        className={classNames(baseClasses, activeClasses, 'w-full text-left')}
        title={collapsed ? item.label : undefined}
      >
        {content}
      </button>
    );
  }

  return (
    <a
      href={item.href}
      className={classNames(baseClasses, activeClasses)}
      title={collapsed ? item.label : undefined}
    >
      {content}
    </a>
  );
}

interface SidebarContentProps {
  items: NavigationItemList;
  pathname: string;
  user: { roles: string[] } | null;
  unreadNotificationsCount: number;
  onInboxClick: () => void;
  collapsed: boolean;
}

function SidebarContent({
  items,
  pathname,
  user,
  unreadNotificationsCount,
  onInboxClick,
  collapsed,
}: SidebarContentProps) {
  // Separate top-level items from sectioned items
  const topLevelIds = ['dashboard', 'inbox', 'noticeboard'];
  const topLevelItems = items.filter((item) => topLevelIds.includes(item.id));
  const sectionedItems = items.filter((item) => !topLevelIds.includes(item.id));

  return (
    <div className='flex grow flex-col overflow-y-auto bg-white border-r border-gray-200'>
      {/* Logo */}
      <div className={classNames('flex h-16 shrink-0 items-center border-b border-gray-100', collapsed ? 'justify-center px-2' : 'px-6')}>
        <a href='/' className='flex items-center'>
          {collapsed ? (
            <img alt='FLOX' src={FLOX_ICON_BLUE_SRC} className='h-8 w-8' />
          ) : (
            <img alt='FLOX' src={FLOX_LOGO_BLUE_SRC} className='h-8 w-auto' />
          )}
        </a>
      </div>

      {/* Navigation */}
      <nav className={classNames('flex flex-1 flex-col py-4', collapsed ? 'px-2' : 'px-3')}>
        {/* Top level items (Dashboard, Inbox, Noticeboard) */}
        <ul className='space-y-1'>
          {topLevelItems.map((item) => {
            if (item.id === 'inbox') {
              return (
                <li key={item.id}>
                  <NavMenuItem
                    item={item}
                    isActive={false}
                    badgeCount={unreadNotificationsCount}
                    onClick={onInboxClick}
                    collapsed={collapsed}
                  />
                </li>
              );
            }
            const isActive = (item.href !== '#' && pathname.startsWith(item.href)) ||
              !!item.activePatterns?.some((pattern) => pathname.startsWith(pattern));
            return (
              <li key={item.id}>
                <NavMenuItem item={item} isActive={isActive} collapsed={collapsed} />
              </li>
            );
          })}
        </ul>

        {/* Sectioned items */}
        <div className='mt-4 space-y-6'>
          {sectionedItems.map((item) => {
            // Skip finance section if user doesn't have permission
            if (
              item.id === 'finance' &&
              !user?.roles.includes('FinanceAdmin')
            ) {
              return null;
            }

            if (item.sublinks) {
              return (
                <div key={item.id}>
                  {/* Section header */}
                  {!collapsed && (
                    <h3 className='px-3 text-xs font-semibold uppercase tracking-wider text-gray-400'>
                      {item.label}
                    </h3>
                  )}
                  {collapsed && (
                    <div className='my-2 mx-2 border-t border-gray-200' />
                  )}
                  {/* Section items */}
                  <ul className={classNames(collapsed ? 'mt-1 space-y-1' : 'mt-2 space-y-1')}>
                    {item.sublinks.map((sublink) => {
                      const isSublinkActive = pathname.startsWith(sublink.href) ||
                        !!sublink.activePatterns?.some((pattern) => pathname.startsWith(pattern));
                      return (
                        <li key={sublink.id}>
                          <NavMenuItem
                            item={sublink as NavigationItem}
                            isActive={isSublinkActive}
                            collapsed={collapsed}
                          />
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            }

            return null;
          })}
        </div>
      </nav>
    </div>
  );
}

export function Navigation({ items, children }: NavigationProps) {
  const { pathname } = useLocation();
  const { user, logOut } = useAuth();
  const { isWarehouseClient } = UserRoleService();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isInboxOpen, setIsInboxOpen] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [showReadOnly, setShowReadOnly] = useState(false);
  const [showMarkAllReadConfirm, setShowMarkAllReadConfirm] = useState(false);
  const [collapsed, setCollapsed] = useAtom(sidebarCollapsedAtom);
  const [notifications, setNotifications] = useAtom(notificationsAtom);
  const unreadNotificationsCount = useAtomValue(unreadNotificationsCountAtom);
  const inboxCategoryFilter = useAtomValue(inboxCategoryFilterAtom);
  const notificationPreferences = useAtomValue(notificationPreferencesAtom);
  const {
    markAsRead,
    markAllAsRead,
    archiveNotification,
    unarchiveNotification,
    deleteNotification,
    snoozeNotification,
    refetch,
  } = useNovuNotifications({
    subscriberId: user?.id!,
    isInitialisingNovu: false,
  });

  const handleMarkAllAsRead = () => {
    markAllAsRead();
    setNotifications(
      notifications.map((n) => ({
        ...n,
        readAt: n.readAt || new Date().toISOString(),
      }))
    );
    setShowMarkAllReadConfirm(false);
  };

  // Track previous filter values to detect actual changes
  const prevFiltersRef = useRef<{
    showArchived: boolean;
    showReadOnly: boolean;
  } | null>(null);

  useEffect(() => {
    if (!isInboxOpen) {
      // Reset filter tracking when inbox closes
      prevFiltersRef.current = null;
      return;
    }

    // Check if filters actually changed (not just inbox opening)
    const filtersChanged =
      prevFiltersRef.current !== null &&
      (prevFiltersRef.current.showArchived !== showArchived ||
        prevFiltersRef.current.showReadOnly !== showReadOnly);

    // Update tracking for next comparison
    prevFiltersRef.current = { showArchived, showReadOnly };

    // Only refetch when filters change, not on initial inbox open
    // Initial open uses notifications already fetched by RequireAuth
    if (filtersChanged) {
      refetch({
        includeArchived: showArchived,
        includeRead: showReadOnly ? false : undefined,
      });
    }
  }, [showArchived, showReadOnly, isInboxOpen, refetch]);

  const filteredNotifications = notifications?.filter((notification) => {
    if (!showArchived && notification?.archivedAt) {
      return false;
    }
    if (showReadOnly && notification?.readAt) {
      return false;
    }
    const category = getNotificationCategory(notification.notificationType);
    if (notificationPreferences?.categoryEnabled?.[category] === false) {
      return false;
    }
    if (inboxCategoryFilter !== 'all' && category !== inboxCategoryFilter) {
      return false;
    }
    return true;
  });

  return (
    <div>
      <Dialog
        open={sidebarOpen}
        onClose={setSidebarOpen}
        className='relative z-navigation lg:hidden'
      >
        <DialogBackdrop
          transition
          className='fixed inset-0 bg-gray-900/80 transition-opacity duration-300 ease-linear data-[closed]:opacity-0'
        />

        <div className='fixed inset-0 flex'>
          <DialogPanel
            transition
            className='relative mr-16 flex w-full max-w-xs flex-1 transform will-change-transform transition duration-300 ease-in-out data-[closed]:-translate-x-full'
          >
            <TransitionChild>
              <div className='absolute left-full top-0 flex w-16 justify-center pt-5 duration-300 ease-in-out data-[closed]:opacity-0'>
                <button
                  type='button'
                  onClick={() => setSidebarOpen(false)}
                  className='-m-2.5 p-2.5'
                >
                  <span className='sr-only'>Close sidebar</span>
                  <XMarkIcon
                    aria-hidden='true'
                    className='h-6 w-6 text-white'
                  />
                </button>
              </div>
            </TransitionChild>
            {/* Mobile sidebar is always expanded */}
            <SidebarContent
              items={items}
              pathname={pathname}
              user={user}
              unreadNotificationsCount={unreadNotificationsCount}
              onInboxClick={() => {
                setIsInboxOpen(!isInboxOpen);
                setSidebarOpen(false);
              }}
              collapsed={false}
            />
          </DialogPanel>
        </div>
      </Dialog>

      {/* Desktop sidebar */}
      <div
        className={classNames(
          'hidden lg:fixed lg:inset-y-0 lg:z-navigation lg:flex lg:flex-col transition-all duration-300 ease-in-out',
          collapsed ? 'lg:w-16' : 'lg:w-64'
        )}
      >
        <SidebarContent
          items={items}
          pathname={pathname}
          user={user}
          unreadNotificationsCount={unreadNotificationsCount}
          onInboxClick={() => setIsInboxOpen(!isInboxOpen)}
          collapsed={collapsed}
        />
        {/* Collapse/expand toggle */}
        <button
          type='button'
          onClick={() => setCollapsed(!collapsed)}
          className='absolute top-4 -right-3.5 z-10 flex h-7 w-7 items-center justify-center rounded-md border border-gray-200 bg-white text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-colors'
          title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? (
            <ExpandIcon className='h-3 w-3' />
          ) : (
            <CollapseIcon className='h-3 w-3' />
          )}
        </button>
      </div>

      {/* Inbox panel */}
      <Transition
        show={isInboxOpen}
        enter='transform transition ease-in-out duration-500 sm:duration-700'
        enterFrom='-translate-x-full'
        enterTo='translate-x-0'
        leave='transform transition ease-in-out duration-500 sm:duration-700'
        leaveFrom='translate-x-0'
        leaveTo='-translate-x-full'
      >
        <div
          className={classNames(
            'hidden lg:fixed lg:inset-y-0 lg:z-inbox lg:flex lg:w-96 lg:flex-col will-change-transform transition-[left] duration-300 ease-in-out',
            collapsed ? 'lg:left-16' : 'lg:left-64'
          )}
          role='dialog'
          aria-modal='true'
        >
          <div className='flex grow flex-col overflow-y-auto border-r border-gray-200 bg-white px-6 pb-4'>
            <TransitionChild>
              <div className='absolute end-0 top-0 flex w-16 justify-center pt-4 duration-300 ease-in-out data-[closed]:opacity-0'>
                <button
                  type='button'
                  onClick={() => setIsInboxOpen(false)}
                  className='-m-2.5 p-2.5'
                >
                  <span className='sr-only'>Close inbox</span>
                  <XMarkIcon
                    aria-hidden='true'
                    className='h-6 w-6 text-black'
                  />
                </button>
              </div>
            </TransitionChild>
            <div className='flex items-center justify-between mt-6 mb-4 pr-10'>
              <h2 className='text-base font-semibold text-gray-900'>Inbox</h2>
              {unreadNotificationsCount > 0 && (
                <button
                  type='button'
                  onClick={() => setShowMarkAllReadConfirm(true)}
                  className='text-xs text-gray-500 hover:text-gray-700'
                >
                  Mark all read
                </button>
              )}
            </div>

            {showMarkAllReadConfirm && (
              <div className='mb-4 p-3 bg-gray-50 rounded-lg border border-gray-200'>
                <p className='text-sm text-gray-700 mb-3'>
                  Mark all {unreadNotificationsCount} notifications as read?
                </p>
                <div className='flex gap-2'>
                  <button
                    type='button'
                    onClick={handleMarkAllAsRead}
                    className='px-3 py-1.5 text-xs font-medium text-white bg-primary hover:bg-primary/90 rounded transition-colors'
                  >
                    Yes, mark all read
                  </button>
                  <button
                    type='button'
                    onClick={() => setShowMarkAllReadConfirm(false)}
                    className='px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 hover:bg-gray-50 rounded transition-colors'
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            <InboxCategoryTabs />

            <div className='flex gap-3 text-xs mt-3 mb-4'>
              <button
                type='button'
                onClick={() => setShowArchived(!showArchived)}
                className={`inline-flex items-center gap-1 px-2 py-1 rounded transition-colors ${
                  showArchived
                    ? 'text-primary bg-primary/5'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <ArchiveBoxIcon className='h-3.5 w-3.5' />
                Archived
              </button>
              <button
                type='button'
                onClick={() => setShowReadOnly(!showReadOnly)}
                className={`inline-flex items-center gap-1 px-2 py-1 rounded transition-colors ${
                  showReadOnly
                    ? 'text-primary bg-primary/5'
                    : 'text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                }`}
              >
                <EyeSlashIcon className='h-3.5 w-3.5' />
                Unread only
              </button>
            </div>

            <div className='-mx-6'>
              {filteredNotifications?.length === 0 ? (
                <div className='py-12 text-center'>
                  <p className='text-gray-400 text-sm'>No notifications</p>
                </div>
              ) : (
                filteredNotifications?.map((notification) => {
                  return (
                    <div key={notification.id}>
                      <NotificationErrorBoundary>
                        <InboxItem
                          notification={notification}
                          notifications={notifications}
                          markAsRead={markAsRead}
                          archiveNotification={archiveNotification}
                          unarchiveNotification={unarchiveNotification}
                          deleteNotification={deleteNotification}
                          snoozeNotification={snoozeNotification}
                          setNotifications={setNotifications}
                          isWarehouseClient={isWarehouseClient(user)}
                          setInboxOpen={setIsInboxOpen}
                          showArchived={showArchived}
                        />
                      </NotificationErrorBoundary>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </Transition>
      {isInboxOpen && (
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div
          onClick={() => setIsInboxOpen(false)}
          className='fixed inset-0 z-10'
        />
      )}

      {/* Main content area */}
      <div
        className={classNames(
          'transition-all duration-300 ease-in-out',
          collapsed ? 'lg:pl-16' : 'lg:pl-64'
        )}
      >
        <div
          className={classNames(
            'fixed top-0 right-0 left-0 z-topBar flex h-16 shrink-0 items-center gap-x-4 border-b border-gray-200 bg-white px-4 shadow-sm sm:gap-x-6 sm:px-6 lg:px-8 transition-all duration-300 ease-in-out',
            collapsed ? 'lg:left-16' : 'lg:left-64'
          )}
        >
          <button
            type='button'
            onClick={() => setSidebarOpen(true)}
            className='-m-2.5 p-2.5 text-gray-700 lg:hidden'
          >
            <span className='sr-only'>Open sidebar</span>
            <Bars3Icon aria-hidden='true' className='h-6 w-6' />
          </button>

          <div aria-hidden='true' className='h-6 w-px bg-gray-200 lg:hidden' />

          <div className='flex flex-1 gap-x-4 self-stretch lg:gap-x-6'>
            <div className='relative flex flex-1'>
              <MacroSearch />
            </div>
            <div className='flex items-center gap-x-4 lg:gap-x-6'>
              <div
                aria-hidden='true'
                className='hidden lg:block lg:h-6 lg:w-px lg:bg-gray-200'
              />
              <Menu as='div' className='relative'>
                <MenuButton className='-m-1.5 flex items-center p-1.5 rounded-lg hover:bg-gray-50 transition-colors'>
                  <span className='sr-only'>Open user menu</span>
                  <span className='h-8 w-8 rounded-full bg-[#000066] flex items-center justify-center lg:hidden'>
                    <span className='text-sm font-semibold text-white'>
                      {user?.name ? user.name.charAt(0).toUpperCase() : 'U'}
                    </span>
                  </span>
                  <span className='hidden lg:flex lg:items-center'>
                    <span
                      aria-hidden='true'
                      className='ml-4 text-sm font-semibold leading-6 text-gray-900 text-left'
                    >
                      {user ? user.name : 'User Menu'}
                      {user?.businessName && (
                        <span className='block text-xs font-normal text-gray-500'>
                          {user.businessName}
                        </span>
                      )}
                    </span>
                    <ChevronDownIcon
                      aria-hidden='true'
                      className='ml-2 h-5 w-5 text-gray-400'
                    />
                  </span>
                </MenuButton>
                <MenuItems
                  modal={false}
                  transition
                  className='absolute right-0 mt-2 w-48 origin-top-right rounded-lg bg-white py-1 shadow-lg ring-1 ring-black/5 transition focus:outline-none data-[closed]:scale-95 data-[closed]:transform data-[closed]:opacity-0 data-[enter]:duration-150 data-[leave]:duration-100 data-[enter]:ease-out data-[leave]:ease-in'
                >
                  {userNavigation.map((item, index) => {
                    if (
                      item.name === 'Integrations' &&
                      !isWarehouseClient(user)
                    ) {
                      return null;
                    }
                    const Icon = item.icon;
                    const isLogout = item.name === 'Log out';
                    const showDivider = isLogout;

                    return (
                      <MenuItem key={item.name}>
                        {({ focus }) => (
                          <>
                            {showDivider && (
                              <div className='my-1 border-t border-gray-100' />
                            )}
                            {isLogout ? (
                              <button
                                type='button'
                                className={classNames(
                                  'flex w-full items-center gap-3 px-4 py-2 text-sm text-gray-700 transition-colors',
                                  focus ? 'bg-gray-50 text-gray-900' : ''
                                )}
                                onClick={logOut}
                              >
                                <Icon className='h-4 w-4 text-gray-400' />
                                {user ? 'Log out' : 'Log in'}
                              </button>
                            ) : (
                              <a
                                href={item.href}
                                className={classNames(
                                  'flex items-center gap-3 px-4 py-2 text-sm text-gray-700 transition-colors',
                                  focus ? 'bg-gray-50 text-gray-900' : ''
                                )}
                              >
                                <Icon className='h-4 w-4 text-gray-400' />
                                {item.name}
                              </a>
                            )}
                          </>
                        )}
                      </MenuItem>
                    );
                  })}
                </MenuItems>
              </Menu>
            </div>
          </div>
        </div>

        <main className='pt-16'>
          <div>{children}</div>
        </main>
      </div>
    </div>
  );
}
